import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { authKeys } from '@/features/auth/api'
import { api, unwrap } from '@/lib/api'
import type { DataEnvelope, Onboarding, OnboardingStepKey } from '@/types/api'

export const onboardingKeys = {
  all: ['onboarding'] as const,
  state: () => [...onboardingKeys.all, 'state'] as const,
}

export const onboardingQueryOptions = queryOptions({
  queryKey: onboardingKeys.state(),
  queryFn: ({ signal }) => unwrap(api.get<DataEnvelope<Onboarding>>('/tenant/onboarding', { signal })),
})

export function useOnboarding(enabled = true) {
  return useQuery({ ...onboardingQueryOptions, enabled })
}

function applyOnboarding(qc: ReturnType<typeof useQueryClient>, data: unknown) {
  // Endpoints may or may not return the updated onboarding; refetch either way.
  if (data && typeof data === 'object' && 'data' in data && (data as DataEnvelope<Onboarding>).data?.steps) {
    qc.setQueryData(onboardingKeys.state(), (data as DataEnvelope<Onboarding>).data)
  }
  void qc.invalidateQueries({ queryKey: onboardingKeys.all })
  void qc.invalidateQueries({ queryKey: authKeys.me() })
  void qc.invalidateQueries({ queryKey: ['dashboard'] })
}

export function useSkipOnboardingStep() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (step: OnboardingStepKey) => api.post<unknown>(`/tenant/onboarding/${step}/skip`),
    onSuccess: (data) => applyOnboarding(qc, data),
  })
}

export function useDismissOnboarding() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.post<unknown>('/tenant/onboarding/dismiss'),
    onMutate: async () => {
      // Optimistically hide the checklist.
      await qc.cancelQueries({ queryKey: onboardingKeys.state() })
      const prev = qc.getQueryData<Onboarding>(onboardingKeys.state())
      if (prev) qc.setQueryData(onboardingKeys.state(), { ...prev, dismissed: true })
      return { prev }
    },
    onError: (_e, _v, ctx) => ctx?.prev && qc.setQueryData(onboardingKeys.state(), ctx.prev),
    onSuccess: (data) => applyOnboarding(qc, data),
  })
}
